// Rules for adding, moving and removing plant. Pure functions: each returns the network as it
// would be after the change, or throws a NetworkError a technician can act on.

import { lineLengthM } from './geo'
import { NetworkError, type NetworkIndex } from './network'
import type { AssetStatus, Cable, CableTier, LngLat, Network, NodeKind, PlantNode, Splitter } from './types'

export const SPLITTER_RATIOS = [2, 4, 8, 16, 32, 64] as const
export const FIBER_COUNTS = [2, 4, 6, 8, 12, 24, 48, 72, 96, 144, 288] as const

export interface NodeDraft {
  kind: Exclude<NodeKind, 'pop'>
  name: string
  location: LngLat
  status: Extract<AssetStatus, 'planned' | 'active'>
  locality?: string
  address?: string
  /** NAPs and LCPs get one splitter of this ratio. Closures, poles and handholes have none. */
  splitterRatio?: number
}

export interface CableDraft {
  name: string
  tier: CableTier
  fromNodeId: string
  toNodeId: string
  fiberCount: number
  status: Extract<AssetStatus, 'planned' | 'active'>
  /** Intermediate route points between the two nodes, as drawn. Ends are taken from the nodes. */
  via: LngLat[]
  measuredLengthM?: number
}

const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 ._\-/#]{1,63}$/

function checkName(net: NetworkIndex, name: string, what: 'node' | 'cable', exceptId?: string) {
  if (!NAME_RE.test(name)) throw new NetworkError(`${what === 'node' ? 'Site' : 'Cable'} name must be 2-64 letters, digits, spaces or . _ - / #`)
  const taken = what === 'node' ? [...net.nodes.values()].some((n) => n.name.toLowerCase() === name.toLowerCase() && n.id !== exceptId) : [...net.cables.values()].some((c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== exceptId)
  if (taken) throw new NetworkError(`${name} already exists`, 'conflict')
}

function checkPoint([lng, lat]: LngLat) {
  if (!(Number.isFinite(lng) && Number.isFinite(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90)) throw new NetworkError('Location is not a valid longitude/latitude')
}

export function addNode(net: NetworkIndex, draft: NodeDraft, ids: { nodeId: string; splitterId: string }): { network: Network; node: PlantNode; splitter?: Splitter } {
  checkName(net, draft.name.trim(), 'node')
  checkPoint(draft.location)
  const needsSplitter = draft.kind === 'nap' || draft.kind === 'lcp'
  if (needsSplitter && !SPLITTER_RATIOS.includes(draft.splitterRatio as (typeof SPLITTER_RATIOS)[number])) {
    throw new NetworkError(`A ${draft.kind.toUpperCase()} needs a splitter ratio (1:${SPLITTER_RATIOS.join(', 1:')})`)
  }
  if (!needsSplitter && draft.splitterRatio !== undefined) throw new NetworkError(`A ${draft.kind} does not hold a splitter; record splitters at NAPs and LCPs`)
  const node: PlantNode = {
    id: ids.nodeId,
    name: draft.name.trim(),
    kind: draft.kind,
    status: draft.status,
    location: [+draft.location[0].toFixed(7), +draft.location[1].toFixed(7)],
    ...(draft.locality?.trim() ? { locality: draft.locality.trim() } : {}),
    ...(draft.address?.trim() ? { address: draft.address.trim() } : {}),
    revision: 1,
  }
  const splitter = needsSplitter ? { id: ids.splitterId, name: `${node.name} 1:${draft.splitterRatio}`, nodeId: node.id, ratio: draft.splitterRatio! } : undefined
  return {
    network: { ...net.network, nodes: [...net.network.nodes, node], splitters: splitter ? [...net.network.splitters, splitter] : net.network.splitters },
    node,
    splitter,
  }
}

export function addCable(net: NetworkIndex, draft: CableDraft, id: string): { network: Network; cable: Cable } {
  checkName(net, draft.name.trim(), 'cable')
  const a = net.nodes.get(draft.fromNodeId)
  const b = net.nodes.get(draft.toNodeId)
  if (!a || !b) throw new NetworkError('Both ends of a cable must be existing sites', 'not_found')
  if (a.id === b.id) throw new NetworkError('A cable must join two different sites')
  if (!FIBER_COUNTS.includes(draft.fiberCount as (typeof FIBER_COUNTS)[number])) throw new NetworkError(`Fibre count must be one of ${FIBER_COUNTS.join(', ')}`)
  if (draft.via.length > 2000) throw new NetworkError('Too many route points')
  draft.via.forEach(checkPoint)
  const path: LngLat[] = [a.location, ...draft.via.map((p): LngLat => [+p[0].toFixed(7), +p[1].toFixed(7)]), b.location]
  const routeM = lineLengthM(path)
  if (routeM > 60_000) throw new NetworkError('Route is longer than 60 km; check the drawn points')
  if (draft.measuredLengthM !== undefined && !(draft.measuredLengthM > 0 && draft.measuredLengthM < 100_000)) throw new NetworkError('Measured length must be between 0 and 100 km')
  const cable: Cable = {
    id,
    name: draft.name.trim(),
    tier: draft.tier,
    status: draft.status,
    fromNodeId: a.id,
    toNodeId: b.id,
    fiberCount: draft.fiberCount,
    fibersPerTube: draft.fiberCount <= 12 ? draft.fiberCount : 12,
    path,
    slackLoops: [],
    overlengthFactor: 0.015,
    ...(draft.measuredLengthM !== undefined ? { measuredLengthM: draft.measuredLengthM } : {}),
  }
  return { network: { ...net.network, cables: [...net.network.cables, cable] }, cable }
}

/** A site can be removed only when nothing depends on it. */
export function removeNode(net: NetworkIndex, nodeId: string): Network {
  const node = net.nodes.get(nodeId)
  if (!node) throw new NetworkError('Site not found', 'not_found')
  if (node.kind === 'pop') throw new NetworkError('The POP cannot be removed here')
  const cables = net.cablesAtNode(nodeId)
  if (cables.length) throw new NetworkError(`${node.name} still has ${cables.length} cable(s): ${cables.slice(0, 3).map((c) => c.name).join(', ')}. Remove them first.`, 'conflict')
  const splitterIds = new Set(net.splittersAtNode(nodeId).map((s) => s.id))
  const subs = [...net.services.values()].filter((s) => splitterIds.has(s.splitterId))
  if (subs.length) throw new NetworkError(`${node.name} still serves ${subs.length} subscriber(s). Move them first.`, 'conflict')
  if (net.connectionsAtNode(nodeId).length) throw new NetworkError(`${node.name} still has splices recorded. Remove them first.`, 'conflict')
  return {
    ...net.network,
    nodes: net.network.nodes.filter((n) => n.id !== nodeId),
    splitters: net.network.splitters.filter((s) => !splitterIds.has(s.id)),
  }
}

/** A cable can be removed only when none of its cores are spliced. */
export function removeCable(net: NetworkIndex, cableId: string): Network {
  const c = net.cables.get(cableId)
  if (!c) throw new NetworkError('Cable not found', 'not_found')
  const spliced = net.network.connections.filter((x) => [x.a, x.b].some((e) => e.kind === 'core' && e.cableId === cableId))
  if (spliced.length) throw new NetworkError(`${c.name} has ${spliced.length} splice(s) on its cores. Remove them first.`, 'conflict')
  return { ...net.network, cables: net.network.cables.filter((x) => x.id !== cableId) }
}

/** Move a site; cables attached to it keep their route and get their end point moved. */
export function moveNode(net: NetworkIndex, nodeId: string, to: LngLat): Network {
  const node = net.nodes.get(nodeId)
  if (!node) throw new NetworkError('Site not found', 'not_found')
  checkPoint(to)
  const loc: LngLat = [+to[0].toFixed(7), +to[1].toFixed(7)]
  return {
    ...net.network,
    nodes: net.network.nodes.map((n) => (n.id === nodeId ? { ...n, location: loc } : n)),
    cables: net.network.cables.map((c) => {
      if (c.fromNodeId !== nodeId && c.toNodeId !== nodeId) return c
      const path = [...c.path]
      if (c.fromNodeId === nodeId) path[0] = loc
      if (c.toNodeId === nodeId) path[path.length - 1] = loc
      return { ...c, path }
    }),
  }
}

/** Suggest the next free name like "NAP-0012" for a kind. */
export function suggestName(net: NetworkIndex, kind: NodeDraft['kind']): string {
  const prefix = { nap: 'NAP', lcp: 'LCP', closure: 'CL', pole: 'POLE', handhole: 'HH' }[kind]
  const taken = new Set([...net.nodes.values()].map((n) => n.name.toUpperCase()))
  for (let i = 1; ; i++) {
    const name = `${prefix}-${String(i).padStart(4, '0')}`
    if (!taken.has(name)) return name
  }
}

export function suggestCableName(net: NetworkIndex, tier: CableTier): string {
  const prefix = { backbone: 'BB', feeder: 'FDR', distribution: 'DST', drop: 'DRP' }[tier]
  const taken = new Set([...net.cables.values()].map((c) => c.name.toUpperCase()))
  for (let i = 1; ; i++) {
    const name = `${prefix}-${String(i).padStart(4, '0')}`
    if (!taken.has(name)) return name
  }
}

/**
 * The next name in a series, keeping the user's own style: "NAP-0007" -> "NAP-0008",
 * "SM-NAP-015" -> "SM-NAP-016", "Pole 9A" -> "Pole 10A", "CL-EAST" -> "CL-EAST-2".
 * Skips names already taken.
 */
export function nextInSeries(name: string, taken: Iterable<string>): string {
  const used = new Set([...taken].map((t) => t.toLowerCase()))
  const m = name.match(/^(.*?)(\d+)(\D*)$/)
  let prefix: string, n: number, width: number, suffix: string
  if (m) {
    prefix = m[1]!
    n = Number(m[2])
    width = m[2]!.length
    suffix = m[3]!
  } else {
    prefix = `${name}-`
    n = 1
    width = 1
    suffix = ''
  }
  for (let i = n + 1; ; i++) {
    const candidate = `${prefix}${String(i).padStart(width, '0')}${suffix}`
    if (!used.has(candidate.toLowerCase())) return candidate
  }
}
